import { z } from "zod";
import { int, real, text } from "./common";

/** How sure the person is about their birth time. */
export const TIME_ACCURACIES = ["exact", "approximate", "unknown"] as const;
export type TimeAccuracy = (typeof TIME_ACCURACIES)[number];

export const TIME_ACCURACY_LABEL: Readonly<Record<TimeAccuracy, string>> = {
  exact: "Exact",
  approximate: "Approximate",
  unknown: "Unknown",
};

export const timeAccuracySchema = z.enum(TIME_ACCURACIES).catch("exact");

/** One result from `GET /geocode`. */
export const geoPlaceSchema = z.object({
  name: text(),
  admin1: text(),
  country: text(),
  lat: real(),
  lon: real(),
  tz: text(),
  geonames_id: int(),
});
export type GeoPlace = Readonly<z.output<typeof geoPlaceSchema>>;

/**
 * "Pune, India" — how a person names a place.
 *
 * The geocoder's region is often a numeric code ("36") and its country an
 * ISO code ("IN"); showing those read as a bug. Codes are dropped or spelled
 * out, and a region is kept only when it is a real name.
 */
export function describePlace(place: GeoPlace): string {
  const region = /^\d+$/.test(place.admin1) || place.admin1 === place.name ? "" : place.admin1;
  return [place.name, region, countryName(place.country)].filter(Boolean).join(", ");
}

function countryName(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return code;
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** The birth details as the backend expects them, snake_case on the wire. */
export const birthDataSchema = z.object({
  year: int(2000),
  month: int(1),
  day: int(1),
  hour: int(12),
  minute: int(0),
  time_accuracy: timeAccuracySchema,
  lat: real(),
  lon: real(),
  resolved_tz: text(),
  raw_place_text: text(),
  geonames_id: int(),
});
export type BirthData = Readonly<z.output<typeof birthDataSchema>>;
